/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { Drawer } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "../../../../Common/Svgs/CloseIcon";
import { updateUsers } from "../../../../Redux/Slices/users.slice";
import capitalize from "../../../../Utils/capitalize";
import { client } from "../../../../Utils/axiosClient";
import StarIcon from "../../../../Common/Svgs/StarIcon";

const Tag = ({ text = "" }) => {
  return (
    <div className="bg-primary-10 py-1 px-2 max-h-[26px] rounded-md max-w-max">
      <p className="font-poppins font-medium text-xs text-primary">{text}</p>
    </div>
  );
};

const LabelAndValue = ({ label = "", value = "", shouldCapitalize = true }) => {
  return (
    <div className="flex-1 font-poppins">
      <p
        className="font-medium text-sm"
        style={{ textTransform: shouldCapitalize && "capitalize" }}
      >
        {value || "--"}
      </p>
      <p className="font-normal text-xs text-dark-gray">{label}</p>
    </div>
  );
};

const DriverDetailDrawer = ({ visible, onCancel }) => {
  const dispatch = useDispatch();

  const { driverDetailDrawer } = useSelector((state) => state.users);

  const data = driverDetailDrawer?.data || {};

  const [driverRating, setDriverRating] = useState([]);

  useEffect(() => {
    try {
      const fetchData = async () => {
        const res = await client.get(
          `${process.env.REACT_APP_API_URL}/drivers/get-rating`,
          {
            params: {
              driverId: data?.id,
            },
          },
        );
        setDriverRating(res?.data?.avgRating);
      };
      if (data?.id) {
        fetchData();
      }
    } catch (error) {
      console.log("error while fetching avg rating ", error);
    }
  }, [data?.id]);

  const handleViewLicense = () => {
    if (data?.licenseUrl) {
      dispatch(
        updateUsers({
          key: "licenseModal",
          value: {
            visible: true,
            url: data?.licenseUrl || "",
            type: "driver",
          },
        }),
      );

      return;
    }

    dispatch(
      updateUsers({
        key: "showToastMessage",
        value: {
          visible: true,
          data: {
            title: "No License Found",
            description: `License not found for driver ${capitalize(
              data?.firstName,
            )} ${capitalize(data?.lastName)}`,
            type: "error",
          },
        },
      }),
    );
  };

  const handleViewFeedback = () => {
    dispatch(
      updateUsers({
        key: "driverFeedbackDrawer",
        value: {
          visible: true,
          driverId: data?.id,
          avgRating: driverRating,
        },
      }),
    );
  };

  return (
    <Drawer
      width={560}
      placement="right"
      closable={false}
      maskStyle={{
        background: "transparent",
        boxShadow: "12px 12px 16px -4px #10182814",
      }}
      open={visible}
      onClose={onCancel}
      className="rounded-tl-xl"
    >
      <div className="p-6 w-full h-max bg-background-white rounded-tl-xl relative">
        <div className="flex items-center justify-between">
          <p className="font-poppins font-medium text-base text-black">
            Driver Details
          </p>

          <CloseIcon
            className="w-5 h-5 cursor-pointer stroke-dark-grey-text"
            onClick={onCancel}
          />
        </div>

        <div className="mt-6 px-6 py-4 border border-light-grey rounded-xl">
          <Tag text="General" />

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue label="First Name" value={data?.firstName} />

            <LabelAndValue label="Last Name" value={data?.lastName} />
          </div>

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue label="Gender" value={data?.gender} />

            <LabelAndValue
              label="Contact Number"
              value={`${data?.contactNo ? `+254 ${data?.contactNo}` : "--"}`}
            />
          </div>

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue
              label="M-Pesa Number"
              value={`${data?.mpesaNo ? `+254 ${data?.mpesaNo}` : "--"}`}
            />

            <LabelAndValue label="Hub Station" value={data?.hubName} />
          </div>
        </div>

        <div className="mt-6 px-6 py-4 border border-light-grey rounded-xl">
          <Tag text="License" />

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue
              label="License Number"
              value={data?.licenseId}
              shouldCapitalize={false}
            />

            <LabelAndValue
              label="Validity"
              value={
                data?.licenseValidity
                  ? dayjs(data?.licenseValidity).format("DD-MM-YYYY")
                  : "--"
              }
            />
          </div>

          <button
            type="button"
            className="mt-4 py-[11px] px-4 bg-primary hover:bg-primary-50 rounded-md"
            onClick={handleViewLicense}
          >
            <p className="font-poppins font-medium text-xs text-white">
              View License
            </p>
          </button>
        </div>

        <div className="mt-6 px-6 py-4 border border-light-grey rounded-xl">
          <Tag text="Credentials" />

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue
              label={data?.appUserId ? "User ID" : "Email ID"}
              value={data?.appUserId || data?.email}
              shouldCapitalize={false}
            />
          </div>
        </div>

        <div className="mt-6 px-6 py-4 border border-light-grey rounded-xl">
          <Tag text="Performance Rating" />

          <div className="mt-4 flex items-center gap-x-4">
            <LabelAndValue
              label="Average"
              value={
                <div className="flex gap-x-1">
                  <StarIcon size={18} fill="#FFD700" stroke="none" />

                  <p className="font-poppins font-medium text-sm">
                    {driverRating || 0}
                  </p>
                </div>
              }
              shouldCapitalize={false}
            />

            <LabelAndValue label="Trips" value={data?.trips} />
          </div>

          <button
            type="button"
            className="mt-4 py-[11px] px-4 bg-primary hover:bg-primary-50 rounded-md"
            onClick={handleViewFeedback}
          >
            <p className="font-poppins font-medium text-xs text-white">
              View Feedback
            </p>
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default DriverDetailDrawer;
